import { useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, EmptyState, ErrorState, Spinner, cx } from '@/components/ui';
import { formatDate } from '@/lib/format';
import { HelpButton } from './HelpButton';
import type { IndicatorHelp } from './indicatorHelp';
import {
  COLLECT_TIME_LABEL,
  SERIES_RANGES,
  US_CANDLE_TIME_LABEL,
  describeSeriesChange,
  formatSeriesValue,
  pointsSince,
  useMarketSeries,
  type MarketSeries,
  type MarketSeriesCode,
} from './marketSeries';
import { usePaperChartColors } from './paperChartColors';

/**
 * 시장지표 하위 탭 하나 — 계열 한두 개(국고채 3년 · 10년 / 코스피 · 코스닥)를 카드로. (2026-09-29 I-81)
 *
 * 카드 하나 = 마지막 값 · 전날 대비 · 기간 처음 대비 · 추이선 · 출처 문구
 * 🔴 출처 문구는 서버가 준다(sourceNote) — KRX 는 약관 제10조③ 으로 "한국거래소 통계정보" 를 밝혀야 한다. 지우지 않는다
 * 등락색은 글자에만(오르면 빨강 · 내리면 파랑) — 추이선은 한 색(환율 탭 RateChart 와 같은 이유)
 */
export function MarketSeriesPanel({
  codes,
  intro,
  help,
}: {
  codes: readonly MarketSeriesCode[];
  intro: string;
  /** 머리 문장 끝의 물음표 — 2026-10-17 국채 탭(국내 / 미국 스위치 · 출처 설명)부터 */
  help?: IndicatorHelp;
}) {
  const [days, setDays] = useState<number>(SERIES_RANGES[0].days);
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-fg-muted">
        {intro}
        {help && <HelpButton help={help} />}
      </p>
      <div className="flex flex-wrap gap-1" role="tablist" aria-label="추이 기간">
        {SERIES_RANGES.map((r) => (
          <button
            key={r.days}
            type="button"
            role="tab"
            aria-selected={days === r.days}
            onClick={() => setDays(r.days)}
            className={cx(
              'min-h-[32px] rounded-full px-3 py-1 text-sm',
              days === r.days ? 'bg-brand text-brand-fg' : 'bg-surface-hover text-fg-muted',
            )}
          >
            {r.label}
          </button>
        ))}
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        {codes.map((code) => (
          <SeriesCard key={code} code={code} days={days} />
        ))}
      </div>
    </div>
  );
}

function SeriesCard({ code, days }: { code: MarketSeriesCode; days: number }) {
  const series = useMarketSeries(code);

  if (series.isPending) {
    return (
      <Card title="시장지표">
        <Spinner />
      </Card>
    );
  }
  if (series.isError) {
    return (
      <Card title="시장지표">
        <ErrorState message="지표를 불러오지 못했습니다." onRetry={() => series.refetch()} />
      </Card>
    );
  }

  const s = series.data;
  const shown = pointsSince(s.points, days);
  const last = s.points[s.points.length - 1] ?? null;
  const prev = s.points[s.points.length - 2] ?? null;
  const first = shown[0] ?? null;
  const dayChange = last ? describeSeriesChange(last.value, prev?.value ?? null, s.unit) : null;
  const rangeChange = last ? describeSeriesChange(last.value, first?.value ?? null, s.unit) : null;
  const rangeLabel = SERIES_RANGES.find((r) => r.days === days)?.label ?? '';

  return (
    <Card title={s.label}>
      {last === null ? (
        /* 배포 직후 첫 수집(매일 09:20) 전이다 — 과거 채우기는 사흘쯤 걸린다(MarketSeriesService 주석) */
        <EmptyState
          title="아직 모은 값이 없습니다"
          description={
            // SPY · QQQ 는 이 탭의 09:20 수집이 아니라 미국 일봉 수집(10:30)에서 온다(2026-10-17) — 시각을 잘못 말하지 않게 가른다
            s.source === 'TOSS'
              ? `미국 일봉(매일 ${US_CANDLE_TIME_LABEL} 수집)에 아직 이 ETF 값이 없습니다.`
              : `매일 ${COLLECT_TIME_LABEL}에 모읍니다. 과거 3년은 처음 며칠에 걸쳐 채워집니다.`
          }
        />
      ) : (
        <div className="flex flex-col gap-2">
          <p className="text-3xl font-semibold tabular-nums">{formatSeriesValue(last.value, s.unit)}</p>
          <p className="text-xs text-fg-subtle">{formatDate(last.date)} 종가 기준</p>
          <dl className="flex flex-col gap-1 text-sm">
            {dayChange && (
              <div className="flex flex-wrap gap-x-2">
                <dt className="shrink-0 text-fg-muted">전 거래일보다</dt>
                <dd className={cx('tabular-nums', toneOf(dayChange.sign))}>{dayChange.text}</dd>
              </div>
            )}
            {rangeChange && (
              <div className="flex flex-wrap gap-x-2">
                <dt className="shrink-0 text-fg-muted">{rangeLabel} 전보다</dt>
                <dd className={cx('tabular-nums', toneOf(rangeChange.sign))}>{rangeChange.text}</dd>
              </div>
            )}
          </dl>
          {shown.length > 1 ? <SeriesChart points={shown} unit={s.unit} label={s.label} /> : null}
        </div>
      )}
      <p className="mt-2 text-xs text-fg-subtle">{s.sourceNote}</p>
    </Card>
  );
}

/** 오르면 빨강 · 내리면 파랑(앱 전체 규칙 · priceDisplay 와 같은 토큰) */
function toneOf(sign: number): string {
  return sign > 0 ? 'text-rise' : sign < 0 ? 'text-fall' : '';
}

/** 추이선 한 줄 — 환율 탭 RateChart 와 같은 모양(한 계열 · 2px · 범례 없음 · 등락색 안 씀) */
function SeriesChart({ points, unit, label }: { points: MarketSeries['points']; unit: string; label: string }) {
  const c = usePaperChartColors();
  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  // 위아래 여유 — 폭의 10%(값이 거의 안 움직이면 아주 작은 여유)
  const pad = Math.max((max - min) * 0.1, Math.abs(max) * 0.001);
  const step = Math.max(1, Math.floor(points.length / 6));
  const ticks = points.filter((_, i) => i % step === 0).map((p) => p.date);

  return (
    <div className="h-56 w-full sm:h-64">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={points} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={c.line} vertical={false} />
          <XAxis
            dataKey="date"
            ticks={ticks}
            interval={0}
            tickFormatter={(d: string) => d.slice(2, 7).replace('-', '.')}
            tick={{ fill: c.axis, fontSize: 11 }}
            stroke={c.line}
          />
          <YAxis
            domain={[min - pad, max + pad]}
            tick={{ fill: c.axis, fontSize: 11 }}
            stroke={c.line}
            width={unit === '원' ? 72 : 52}
            tickFormatter={(v: number) =>
              unit === '%' ? v.toFixed(2) : Math.round(v).toLocaleString('ko-KR')}
          />
          <Tooltip
            cursor={{ stroke: c.axis, strokeDasharray: '3 3' }}
            labelFormatter={(d) => formatDate(String(d))}
            formatter={(value) => [formatSeriesValue(Number(value), unit), label]}
            contentStyle={{ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 8, fontSize: 12 }}
          />
          <Line
            dataKey="value"
            stroke={c.safe}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, stroke: c.surface, strokeWidth: 2 }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
